// ============================================================================
// TEMPLATE BUILDER PAGE
// Multi-step wizard for creating/editing session templates
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Save,
  X,
  Send,
  FileText,
  Layers,
  CheckCircle,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  SessionTemplate,
  CreateSessionTemplateInput,
  PhaseReference,
  TemplateType,
  TemplateScope
} from '../types';
import {
  createSessionTemplate,
  updateSessionTemplate,
  getSessionTemplate,
  submitTemplateForReview
} from '../services/templateService';
import TemplateBasicInfo from '../components/template/TemplateBasicInfo';
import PhaseLibrarySelector from '../components/template/PhaseLibrarySelector';

type BuilderStep = 'basic' | 'phases' | 'review';

interface TemplateBuilderState {
  // Basic Info
  name: string;
  description: string;
  purpose: string;
  goal: string;
  category: TemplateType;
  scope: TemplateScope;

  // Phase Selection
  phaseReferences: PhaseReference[];
}

const STEPS: Array<{ key: BuilderStep; label: string; icon: React.ReactNode }> = [
  { key: 'basic', label: 'Basic Information', icon: <FileText className="w-4 h-4" /> },
  { key: 'phases', label: 'Phase Selection', icon: <Layers className="w-4 h-4" /> },
  { key: 'review', label: 'Review & Create', icon: <CheckCircle className="w-4 h-4" /> }
];

export default function TemplateBuilder() {
  const navigate = useNavigate();
  const location = useLocation();
  const { templateId } = useParams<{ templateId: string }>();
  const { user } = useAuth();

  const isEditMode = !!templateId;
  const cloneData = location.state?.cloneFrom as SessionTemplate | undefined;
  const cloneName = location.state?.name as string | undefined;

  // Wizard state
  const [currentStep, setCurrentStep] = useState<BuilderStep>('basic');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingTemplate, setExistingTemplate] = useState<SessionTemplate | null>(null);

  // Submit for Review modal state
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitTargetScope, setSubmitTargetScope] = useState<'ORGANIZATION' | 'SYSTEM'>('ORGANIZATION');
  const [submitNotes, setSubmitNotes] = useState('');

  // Form state
  const [formData, setFormData] = useState<TemplateBuilderState>({
    name: '',
    description: '',
    purpose: '',
    goal: '',
    category: TemplateType.WORKSHOP,
    scope: TemplateScope.USER,
    phaseReferences: []
  });

  // Load existing template if in edit mode
  useEffect(() => {
    if (isEditMode && templateId) {
      loadExistingTemplate(templateId);
    } else if (cloneData) {
      setFormData({
        name: cloneName || cloneData.name + ' (Copy)',
        description: cloneData.description,
        purpose: cloneData.purpose,
        goal: cloneData.goal,
        category: cloneData.category,
        scope: TemplateScope.USER,
        phaseReferences: cloneData.phaseReferences
      });
    }
  }, [isEditMode, templateId, cloneData, cloneName]);

  const loadExistingTemplate = async (id: string) => {
    try {
      setLoading(true);
      const template = await getSessionTemplate(id);
      setExistingTemplate(template);

      setFormData({
        name: template.name,
        description: template.description,
        purpose: template.purpose,
        goal: template.goal,
        category: template.category,
        scope: template.scope,
        phaseReferences: template.phaseReferences
      });
    } catch (err: any) {
      setError(`Failed to load template: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBasicInfoChange = (updates: Partial<TemplateBuilderState>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handlePhaseReferencesChange = (phases: PhaseReference[]) => {
    setFormData(prev => ({ ...prev, phaseReferences: phases }));
  };

  const canProceed = (step: BuilderStep): boolean => {
    switch (step) {
      case 'basic':
        return !!(
          formData.name &&
          formData.description &&
          formData.purpose &&
          formData.goal &&
          formData.category
        );
      case 'phases':
        return formData.phaseReferences.length > 0;
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const currentIndex = STEPS.findIndex(s => s.key === currentStep);

  const handleNext = () => {
    if (currentIndex < STEPS.length - 1 && canProceed(currentStep)) {
      setCurrentStep(STEPS[currentIndex + 1].key);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentStep(STEPS[currentIndex - 1].key);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      const input: CreateSessionTemplateInput = {
        name: formData.name,
        description: formData.description,
        purpose: formData.purpose,
        goal: formData.goal,
        category: formData.category,
        scope: formData.scope,
        ownerId: user?.userId || '',
        createdBy: user?.userId || '',
        phaseReferences: formData.phaseReferences,
        outputDefinitions: {
          outputs: []
        }
      };

      if (isEditMode && templateId) {
        const changeNotes = window.prompt('Change notes (optional):') || undefined;
        await updateSessionTemplate(templateId, { ...input, changeNotes });
        alert('Template updated successfully!');
      } else {
        await createSessionTemplate(input);
        alert('Template created successfully!');
      }

      navigate('/templates');
    } catch (err: any) {
      setError(`Failed to save template: ${err.message}`);
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!existingTemplate) {
      alert('Please save the template before submitting for review');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await submitTemplateForReview(
        'SESSION',
        existingTemplate.sessionTemplateId,
        existingTemplate.versionNumber,
        submitTargetScope,
        submitNotes || undefined
      );

      setShowSubmitModal(false);
      alert('Template submitted for review successfully!');
      navigate('/templates');
    } catch (err: any) {
      setError(`Failed to submit for review: ${err.message}`);
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (cat: TemplateType) => {
    const labels: Record<string, string> = {
      WORKSHOP: 'Workshop',
      BRAINSTORM: 'Brainstorm',
      REVIEW: 'Review',
      PLANNING: 'Planning',
      CUSTOM: 'Custom'
    };
    return labels[cat] || cat;
  };

  const getScopeLabel = (scope: TemplateScope) => {
    const labels: Record<string, string> = {
      SESSION: 'Session (one-time use)',
      USER: 'User (personal library)',
      ORGANIZATION: 'Organization'
    };
    return labels[scope] || scope;
  };

  if (loading && !formData.name) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-text-sub text-sm">Loading template...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light">
      <div className="w-full max-w-[900px] mx-auto px-6 py-8 pb-28">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-text-sub mb-6">
          <button
            onClick={() => navigate('/templates')}
            className="hover:text-primary transition-colors"
          >
            Templates
          </button>
          <ChevronRight className="w-4 h-4" />
          <span className="text-text-main font-medium">
            {isEditMode ? 'Edit Template' : 'Create New Template'}
          </span>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-main mb-2">
            {isEditMode ? 'Edit Template' : 'Create Session Template'}
          </h1>
          <p className="text-text-sub">
            {isEditMode
              ? 'Update your session template configuration'
              : 'Design a reusable template for consistent session facilitation'}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <X className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800">Error</p>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-0 mb-8">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.key}>
              <button
                onClick={() => {
                  // Allow clicking on completed steps or current step
                  if (index <= currentIndex) {
                    setCurrentStep(step.key);
                  }
                }}
                className={`flex items-center gap-2.5 ${
                  index <= currentIndex ? 'cursor-pointer' : 'cursor-default'
                }`}
              >
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                    index < currentIndex
                      ? 'bg-primary text-white'
                      : index === currentIndex
                      ? 'bg-primary text-white'
                      : 'bg-slate-200 text-slate-400'
                  }`}
                >
                  {index < currentIndex ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    index + 1
                  )}
                </div>
                <span
                  className={`text-sm font-medium hidden sm:block ${
                    index === currentIndex
                      ? 'text-text-main'
                      : index < currentIndex
                      ? 'text-primary'
                      : 'text-slate-400'
                  }`}
                >
                  {step.label}
                </span>
              </button>
              {index < STEPS.length - 1 && (
                <div
                  className={`w-12 h-0.5 mx-3 ${
                    index < currentIndex ? 'bg-primary' : 'bg-slate-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        {currentStep === 'basic' && (
          <TemplateBasicInfo
            data={formData}
            onChange={handleBasicInfoChange}
          />
        )}

        {currentStep === 'phases' && (
          <PhaseLibrarySelector
            selectedPhases={formData.phaseReferences}
            onChange={handlePhaseReferencesChange}
          />
        )}

        {currentStep === 'review' && (
          <div className="flex flex-col gap-6">
            {/* Basic Info Summary Card */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-text-main">Template Summary</h3>
                  <p className="text-sm text-text-sub">Review your template details before creating.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="col-span-2">
                  <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Name</p>
                  <p className="text-sm text-text-main font-medium">{formData.name}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Description</p>
                  <p className="text-sm text-text-main">{formData.description}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Purpose</p>
                  <p className="text-sm text-text-main">{formData.purpose}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Goal</p>
                  <p className="text-sm text-text-main">{formData.goal}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Category</p>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary">
                    {getCategoryLabel(formData.category)}
                  </span>
                </div>
                <div>
                  <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Scope</p>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-600">
                    {getScopeLabel(formData.scope)}
                  </span>
                </div>
              </div>
            </div>

            {/* Phases Summary Card */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Layers className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-text-main">Session Phases</h3>
                    <p className="text-sm text-text-sub">Phases will run in the order shown below.</p>
                  </div>
                </div>
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary">
                  {formData.phaseReferences.length} phase{formData.phaseReferences.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="space-y-2">
                {[...formData.phaseReferences]
                  .sort((a, b) => a.orderIndex - b.orderIndex)
                  .map((phase, index) => (
                    <div
                      key={phase.phaseTemplateId}
                      className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100"
                    >
                      <div className="w-7 h-7 rounded-md bg-primary text-white flex items-center justify-center font-bold text-xs flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-main">
                          Phase {phase.phaseTemplateId}
                        </p>
                        <p className="text-xs text-text-sub">v{phase.versionNumber}</p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-md border-t border-slate-200 p-4 z-10">
        <div className="max-w-[900px] mx-auto flex items-center justify-between">
          <div>
            {currentStep !== 'basic' && (
              <button
                onClick={handleBack}
                className="px-5 py-2.5 rounded-lg border border-slate-300 text-text-main hover:bg-slate-50 font-medium transition-colors text-sm flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/templates')}
              className="px-5 py-2.5 rounded-lg border border-slate-300 text-text-main hover:bg-slate-50 font-medium transition-colors text-sm"
            >
              Cancel
            </button>

            {currentStep !== 'review' ? (
              <button
                onClick={handleNext}
                disabled={!canProceed(currentStep)}
                className={`px-6 py-2.5 rounded-lg font-medium shadow-sm transition-colors text-sm flex items-center gap-2 ${
                  canProceed(currentStep)
                    ? 'bg-primary hover:bg-primary/90 text-white'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                Next: {STEPS[currentIndex + 1]?.label}
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <>
                {isEditMode && existingTemplate && (
                  <button
                    onClick={() => setShowSubmitModal(true)}
                    disabled={loading}
                    className="px-5 py-2.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium shadow-sm transition-colors text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4" />
                    Submit for Review
                  </button>
                )}
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="px-6 py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-white font-medium shadow-sm transition-colors text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4" />
                  {loading ? 'Saving...' : isEditMode ? 'Update Template' : 'Create Template'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Submit for Review Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-[90%] shadow-2xl">
            <h2 className="text-xl font-bold text-text-main mb-5">
              Submit Template for Review
            </h2>

            <div className="mb-5">
              <label className="flex flex-col gap-2">
                <span className="text-sm font-medium text-text-main">
                  Target Scope <span className="text-red-500">*</span>
                </span>
                <select
                  value={submitTargetScope}
                  onChange={(e) => setSubmitTargetScope(e.target.value as 'ORGANIZATION' | 'SYSTEM')}
                  className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm h-11"
                >
                  <option value="ORGANIZATION">Organization (Share within organization)</option>
                  <option value="SYSTEM">System (Make public/prebuilt)</option>
                </select>
              </label>
              <p className="text-xs text-text-sub mt-2">
                {submitTargetScope === 'ORGANIZATION'
                  ? 'Submit to make this template available to your entire organization'
                  : 'Submit to make this template available system-wide as a prebuilt template'}
              </p>
            </div>

            <div className="mb-6">
              <label className="flex flex-col gap-2">
                <span className="text-sm font-medium text-text-main">
                  Submission Notes (Optional)
                </span>
                <textarea
                  value={submitNotes}
                  onChange={(e) => setSubmitNotes(e.target.value)}
                  placeholder="Add any notes for the reviewer..."
                  rows={3}
                  className="w-full rounded-lg border-slate-300 focus:border-primary focus:ring-primary shadow-sm resize-vertical"
                />
              </label>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowSubmitModal(false);
                  setSubmitNotes('');
                }}
                disabled={loading}
                className="px-5 py-2.5 rounded-lg border border-slate-300 text-text-main hover:bg-slate-50 font-medium transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitForReview}
                disabled={loading}
                className="px-5 py-2.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium shadow-sm transition-colors text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                {loading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
